import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon, ArrowUpIcon, RefreshCwIcon } from 'lucide-react';
import { InteriorSlide } from './SlideShell';
import type { SlideProps } from './SlideShell';
const HOOK_SCREEN = {
  src: 'https://cdn.magicpatterns.com/patterns/generated-images/8c12d81b-81c9-4950-a9df-8b72bc22f05b.jpg',
  alt: 'WhatsApp conversation showing a brand banner ad reading “Seven minutes. One choice.”'
};
const TEASER_SCREENS = [{
  src: 'https://cdn.magicpatterns.com/patterns/generated-images/f56b4488-53cb-4caa-ae80-a720cbf1c3eb.jpg',
  alt: 'Teaser Snak opening screen inviting the HCP to begin the case',
  offset: 'translate-y-4'
}, {
  src: 'https://cdn.magicpatterns.com/patterns/generated-images/58369ee1-e4a5-4920-bde3-b70e71b20ceb.jpg',
  alt: 'Teaser Snak asking the HCP what they would prescribe, with three answer options',
  offset: '-translate-y-2'
}, {
  src: 'https://cdn.magicpatterns.com/patterns/generated-images/a9602e53-2402-4752-ac16-e8b3f93684ea.jpg',
  alt: 'Teaser Snak answer reveal comparing the HCP’s choice with peer responses',
  offset: 'translate-y-3'
}];
const INSIGHTS_SCREEN = {
  src: 'https://cdn.magicpatterns.com/patterns/generated-images/e0f59a14-3932-4db7-bd6d-4e3215b287c2.jpg',
  alt: 'Campaign analytics dashboard showing engagement, answer distribution and campaign performance'
};
const STEPS = [{
  step: '01',
  label: 'Hook',
  detail: 'A WhatsApp banner ad as the visual entry point — placed where HCPs already are.'
}, {
  step: '02',
  label: 'Teaser Snak',
  detail: 'A specific microsite created per campaign: a case, a choice, and an immediate peer-benchmarked answer.'
}, {
  step: '03',
  label: 'Monitoring & optimization',
  detail: 'Every tap becomes data — capture, learn, and sharpen the next campaign.'
}];
export function SlideCampaignFlowMockups({}: SlideProps) {
  return <InteriorSlide surface data-id="element-297" data-mp="so97rpjw597x">
      <motion.header initial={{
      opacity: 0,
      y: 16
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.5
    }} className="mb-[clamp(0.75rem,1.5vw,1.5rem)] flex flex-shrink-0 items-end justify-between gap-8" data-id="element-298" data-mp="1vwtrqky23ody">
        <div data-id="element-299" data-mp="24zbh3890cx56">
          <p className="mb-2 text-[clamp(0.7rem,1vw,0.9rem)] font-bold uppercase tracking-[0.2em] text-[var(--brand)]" data-id="element-300" data-mp="24vnkg089num8">
            Turn-key campaign model
          </p>
          <h2 className="text-[clamp(1.9rem,3.1vw,3rem)] font-extrabold leading-tight tracking-[-0.035em] text-[var(--fg)]" data-id="element-301" data-mp="2eumvfpgi82ce">
            Banner to Snak to insight—
            <span className="text-[var(--brand)]" data-id="element-302" data-mp="2np17lj5qmwra">three steps, one loop.</span>
          </h2>
        </div>
        <p className="hidden max-w-sm text-right text-sm leading-relaxed text-[var(--muted)] lg:block" data-id="element-303" data-mp="2jmo5d4q79gz9">
          The hook earns the tap, the Snak earns the answer, the data briefs the
          next campaign.
        </p>
      </motion.header>

      <div className="flex min-h-0 flex-1 flex-col" data-id="element-304" data-mp="2spff903fg4os">
        <div className="flex min-h-0 flex-1 items-center gap-[clamp(0.5rem,1.2vw,1.25rem)]" data-id="element-305" data-mp="3c94obmtqm2r1">
          {/* Hook */}
          <motion.figure initial={{
          opacity: 0,
          y: 22
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.16,
          duration: 0.55
        }} className="flex h-full min-w-0 flex-[2] items-center justify-center" data-id="element-306" data-mp="3h765u3bilu0e">
            <img src={HOOK_SCREEN.src} alt={HOOK_SCREEN.alt} className="max-h-full w-auto rounded-[1.25rem] object-contain drop-shadow-[0_18px_40px_rgba(23,32,51,0.18)]" data-id="element-307" data-mp="2tj6f33e7e3lt" />
          </motion.figure>

          <motion.span initial={{
          opacity: 0
        }} animate={{
          opacity: 1,
          x: [0, 5, 0]
        }} transition={{
          opacity: {
            delay: 0.34,
            duration: 0.4
          },
          x: {
            duration: 1.8,
            repeat: Infinity,
            ease: 'easeInOut'
          }
        }} className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-[var(--brand)] text-white shadow-sm" aria-hidden="true" data-id="element-308" data-mp="2jma1wkdd0tax">
            <ArrowRightIcon className="h-4 w-4" data-id="element-309" data-mp="22it90irjvjr3" />
          </motion.span>

          {/* Teaser screens */}
          <div className="flex h-full min-w-0 flex-[5] items-center justify-center gap-[clamp(0.35rem,0.8vw,0.85rem)]" data-id="element-310" data-mp="1xrupzbf6g7k5">
            {TEASER_SCREENS.map((screen, index) => <motion.figure key={screen.src} initial={{
            opacity: 0,
            y: 26
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.3 + index * 0.1,
            duration: 0.55
          }} className={`flex h-full min-w-0 flex-1 items-center justify-center ${screen.offset}`} data-id="element-311" data-mp="1orof7xav5loo" data-mpi={index}>
                <img src={screen.src} alt={screen.alt} className="max-h-full w-auto rounded-[1.25rem] object-contain drop-shadow-[0_18px_40px_rgba(23,32,51,0.16)]" data-id="element-312" data-mp="19w8gvg3933mh" />
              </motion.figure>)}
          </div>

          <motion.span initial={{
          opacity: 0
        }} animate={{
          opacity: 1,
          x: [0, 5, 0]
        }} transition={{
          opacity: {
            delay: 0.6,
            duration: 0.4
          },
          x: {
            duration: 1.8,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 0.4
          }
        }} className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-[var(--brand)] text-white shadow-sm" aria-hidden="true" data-id="element-313" data-mp="3coxmkvynmzyc">
            <ArrowRightIcon className="h-4 w-4" data-id="element-314" data-mp="32rj5cu4wq74z" />
          </motion.span>

          {/* Insights */}
          <motion.figure initial={{
          opacity: 0,
          y: 22
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.62,
          duration: 0.55
        }} className="flex h-full min-w-0 flex-[3] items-center justify-center" data-id="element-315" data-mp="37qmding6oth6">
            <img src={INSIGHTS_SCREEN.src} alt={INSIGHTS_SCREEN.alt} className="max-h-[78%] w-auto rounded-[1rem] object-contain ring-1 ring-[var(--fg)]/10 drop-shadow-[0_18px_40px_rgba(23,32,51,0.16)]" data-id="element-316" data-mp="myu195c00c9q" />
          </motion.figure>
        </div>

        {/* Closed improvement loop: insights travel back into the next hook */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 1,
        duration: 0.5
      }} className="relative mx-[clamp(1rem,3vw,3rem)] mt-[clamp(0.5rem,1vw,1rem)] h-[clamp(2.25rem,4vh,3.25rem)] flex-shrink-0" data-id="element-317" data-mp="2cdtnelsctft">
          {/* The loop path: down from insights, back across, up into the hook */}
          <div className="absolute inset-x-0 top-0 h-full rounded-b-[2rem] border-b-[3px] border-l-[3px] border-r-[3px] border-dashed border-[var(--accent)]" data-id="element-318" data-mp="3fqil67i5elgo" />

          {/* Arrowhead re-entering the hook */}
          <span className="absolute -top-2 left-0 flex h-6 w-6 -translate-x-1/2 items-center justify-center rounded-full bg-[var(--accent)] text-white shadow-md" aria-hidden="true" data-id="element-319" data-mp="2n23aty9yorbo">
            <ArrowUpIcon className="h-3.5 w-3.5" data-id="element-320" data-mp="17bar2pauctsu" />
          </span>

          {/* Departure point at the insights end */}
          <span className="absolute -top-1.5 right-0 h-3 w-3 translate-x-1/2 rounded-full bg-[var(--accent)]" aria-hidden="true" data-id="element-321" data-mp="smxr7h8muqt2" />

          {/* Insight travelling back around the loop */}
          <motion.span animate={{
          left: ['98%', '2%'],
          opacity: [0, 1, 1, 0]
        }} transition={{
          duration: 3.6,
          repeat: Infinity,
          ease: 'easeInOut',
          times: [0, 0.1, 0.9, 1]
        }} className="absolute -bottom-[6px] h-3 w-3 rounded-full bg-[var(--accent)] shadow-[0_0_0_5px_rgba(106,44,145,0.18)]" aria-hidden="true" data-id="element-322" data-mp="1l87hwqqymb9q" />

          <span className="absolute -bottom-3.5 left-1/2 flex -translate-x-1/2 items-center gap-2 whitespace-nowrap rounded-full bg-[var(--accent)] px-4 py-1.5 text-[clamp(0.62rem,0.85vw,0.78rem)] font-bold text-white shadow-md" data-id="element-323" data-mp="4w0s551xrnz8">
            <motion.span animate={{
            rotate: 360
          }} transition={{
            duration: 5,
            repeat: Infinity,
            ease: 'linear'
          }} className="flex" aria-hidden="true" data-id="element-324" data-mp="ripbjcz07m8s">
              <RefreshCwIcon className="h-3.5 w-3.5" data-id="element-325" data-mp="ilnepnxyjah6" />
            </motion.span>
            Continuous loop — every answer sharpens next month’s hook and
            question
          </span>
        </motion.div>

        {/* Captions */}
        <div className="mt-[clamp(1.25rem,2.2vw,2rem)] flex flex-shrink-0 gap-[clamp(0.75rem,1.5vw,1.5rem)]" data-id="element-326" data-mp="16gzrldy8xb0p">
          {STEPS.map((item, index) => <motion.div key={item.label} initial={{
          opacity: 0,
          y: 14
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.75 + index * 0.1,
          duration: 0.45
        }} className={`min-w-0 ${index === 0 ? 'flex-[2]' : index === 1 ? 'flex-[5]' : 'flex-[3]'}`} data-id="element-327" data-mp="11i6h7ue9a04r" data-mpi={index}>
              <p className="flex items-baseline gap-2 text-[clamp(0.9rem,1.25vw,1.15rem)] font-extrabold leading-tight text-[var(--fg)]" data-id="element-328" data-mp="26iegv0h3z06s">
                <span className="text-[10px] font-bold tracking-[0.2em] text-[var(--brand)]" data-id="element-329" data-mp="2biitugit5cs9">
                  {item.step}
                </span>
                {item.label}
              </p>
              <p className="mt-1 text-[clamp(0.7rem,0.95vw,0.9rem)] leading-snug text-[var(--muted)]" data-id="element-330" data-mp="2kf0au0dus4vx">
                {item.detail}
              </p>
            </motion.div>)}
        </div>
      </div>
    </InteriorSlide>;
}